import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Language from "../lib/Language";
import ContextMenu from "../lib/ContextMenu";
import { TObjectFieldClassWithName } from "./ObjectSelect";

const DISABLED_DELAY_MS = 300;

const useDelayedTrue = (value: boolean, delayMs: number): boolean => {
    const [delayed, setDelayed] = useState(false);

    useEffect(() => {
        if (!value) {
            setDelayed(false);
            return;
        }
        const timer = setTimeout(() => setDelayed(true), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);

    return delayed;
};

type TObjectSelectTopLineProps = {
    disabled: boolean;
    topLeftButton: React.ReactNode;
    hasSearchFields: boolean;
    hasSearchSettings: boolean;
    className: string;
    buttonClasses: string[];
    hasType: boolean;
    toggleSettings: (event: React.BaseSyntheticEvent) => void;
    onSearchKeyPress: (event: React.BaseSyntheticEvent) => void;
    onSearchStringChange: (event: React.BaseSyntheticEvent) => void;
    clearSearchString: () => void;
    showFieldSelection: (event: React.BaseSyntheticEvent) => void;
    onSearchSettingsChange: (settings: any) => void;
    selectedSearchName: string;
    onNamedSearchSelected: (event: React.BaseSyntheticEvent) => void;
    namedSearches: any[];
    onSendToObjectHeader: (objects: TObjectFieldClassWithName[], doSet?: boolean) => void;
    getSelectedObjects: (
        cb: (objects: TObjectFieldClassWithName[], doSet?: boolean) => void,
        doSet?: boolean
    ) => void;
    onSendToObjectManager: () => void;
    allowMultiSelection?: boolean;
    onMultipleSelected?: boolean;
    onAddSelectedObjects: (addAll: boolean) => void;
    focusSearchToken: number;
};

const ObjectSelectTopLine = forwardRef((props: TObjectSelectTopLineProps, ref) => {
    const disabled = useDelayedTrue(props.disabled, DISABLED_DELAY_MS);

    const clearSettingsButton = props.hasSearchSettings ? (
        <button
            disabled={disabled}
            title={Language.get("nc_dynamic_object_list_clear_search_settings")}
            className="deprecated clearObjectSearch"
            onClick={() => {
                props.onSearchSettingsChange({});
            }}
        />
    ) : null;

    const savedSettings =
        props.hasType && props.namedSearches.length > 0 ? (
            <select
                disabled={disabled}
                value={props.selectedSearchName}
                onChange={props.onNamedSearchSelected}
            >
                <option key="noNamedSearchSelected" value="">
                    -
                </option>
                {props.namedSearches.map((namedSearch) => (
                    <option key={namedSearch.name} value={namedSearch.name}>
                        {namedSearch.name}
                    </option>
                ))}
            </select>
        ) : null;

    const sendToObjectHeaderButton = props.onSendToObjectHeader ? (
        <button
            disabled={disabled}
            title={Language.get("nc_set_objects_in_object_header")}
            className="deprecated sendObjects"
            onClick={(event) => {
                ContextMenu.displayMenu(
                    [
                        {
                            key: "setObjects",
                            label: Language.get("nc_set_objects_in_object_header"),
                            action: () =>
                                props.getSelectedObjects(props.onSendToObjectHeader, true),
                        },
                        {
                            key: "addObjects",
                            label: Language.get("nc_add_objects_to_object_header"),
                            action: () =>
                                props.getSelectedObjects(props.onSendToObjectHeader, false),
                        },
                    ],
                    event
                );
            }}
        />
    ) : null;

    const sendToObjectManagerButton = props.onSendToObjectManager ? (
        <button
            disabled={disabled}
            title={Language.get("nc_send_to_object_manager")}
            className="deprecated sendObjects"
            onClick={() => {
                props.getSelectedObjects(props.onSendToObjectManager);
            }}
        />
    ) : null;

    const sendToSelectionListButton =
        props.allowMultiSelection && props.onMultipleSelected ? (
            <button
                disabled={disabled}
                title={Language.get("nc_add_to_selection")}
                className="deprecated sendObjectsSelection"
                onClick={(event) => {
                    ContextMenu.displayMenu(
                        [
                            {
                                key: "setObjects",
                                label: Language.get("nc_add_selected_objects"),
                                action: () => props.onAddSelectedObjects(false),
                            },
                            {
                                key: "addObjects",
                                label: Language.get("nc_add_all_objects"),
                                action: () => props.onAddSelectedObjects(true),
                            },
                        ],
                        event
                    );
                }}
            />
        ) : null;

    const searchRef = useRef<HTMLInputElement>(null);
    const settingsButtonRef = useRef<HTMLButtonElement>(null);
    const searchFieldButtonRef = useRef<HTMLButtonElement>(null);

    const searchDisabled = !props.hasType || disabled;

    // Focus search field on request, once it becomes enabled
    useEffect(() => {
        if (!props.focusSearchToken || searchDisabled) {
            return;
        }
        searchRef.current?.focus();
        searchRef.current?.select();
    }, [props.focusSearchToken, searchDisabled]);

    useImperativeHandle(ref, () => ({
        focusSearch: () => searchRef.current?.focus(),
        blurSearch: () => searchRef.current?.blur(),
        selectSearch: () => searchRef.current?.select(),
        setSearchValue: (val) => {
            if (searchRef.current) {
                searchRef.current.value = val;
            }
        },
        getSearchValue: () => searchRef.current?.value,
        getSettingsButtonOffsetParent: () => settingsButtonRef.current?.offsetParent,
        getSearchFieldButtonBoundingClientRect: () =>
            searchFieldButtonRef.current?.getBoundingClientRect(),
    }));

    return (
        <div className="listTopLine">
            {props.topLeftButton}
            <button
                ref={settingsButtonRef}
                onClick={props.toggleSettings}
                className={`deprecated ${props.buttonClasses.join(" ")}`}
                disabled={!props.hasType || disabled}
            />
            {clearSettingsButton}
            {savedSettings}
            <div className="searchContainer">
                <input
                    ref={searchRef}
                    type="text"
                    className="search"
                    disabled={searchDisabled}
                    onKeyPress={props.onSearchKeyPress}
                    onChange={props.onSearchStringChange}
                />
                {props.hasSearchFields ? (
                    <button
                        disabled={disabled}
                        ref={searchFieldButtonRef}
                        className={`deprecated ${props.className}`}
                        onClick={props.showFieldSelection}
                        title={Language.get("nc_object_list_search_fields")}
                    />
                ) : null}
                <button
                    disabled={disabled}
                    className="deprecated clearSearch"
                    onClick={props.clearSearchString}
                />
            </div>
            {sendToObjectHeaderButton}
            {sendToObjectManagerButton}
            {sendToSelectionListButton}
        </div>
    );
});

export default ObjectSelectTopLine;
